import { createContext, useContext, useState, useEffect } from 'react';
import { Session, User, AuthChangeEvent } from '@supabase/supabase-js';
import { useToast } from '@/hooks/use-toast';
import { getSupabaseClient } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

type Provider = 'google' | 'github' | 'facebook' | 'twitter' | 'apple';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  initError: Error | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  handleOAuthLogin: (provider: Provider) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState<Error | null>(null);

  // Initialize auth state
  useEffect(() => {
    const setupAuth = async () => {
      try {
        const supabase = await getSupabaseClient();

        // First set up the auth state listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          (event: AuthChangeEvent, currentSession: Session | null) => {
            console.log('Auth state changed:', event);
            setSession(currentSession);
            setUser(currentSession?.user ?? null);

            // Handle initial session setup or session refresh
            if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
              setLoading(false);
            }

            // Handle signed in event - don't do navigation here to avoid loops
            if (event === 'SIGNED_IN') {
              console.log('User signed in, session established');
            }

            // Handle signed out
            if (event === 'SIGNED_OUT') {
              console.log('User signed out, session cleared');
              // Navigate to home page on sign out
              navigate('/');
            }
          }
        );

        // Then check for existing session
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        setSession(data.session);
        setUser(data.session?.user ?? null);

        // Clean up subscription on unmount
        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Error setting up auth provider:', error);
        setInitError(error instanceof Error ? error : new Error('Failed to initialize authentication'));
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    setupAuth();
  }, [navigate]);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const supabase = await getSupabaseClient();
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        console.error('Sign in error:', error);
        throw error;
      }
      
      // Success toast
      toast({
        title: "Signed in successfully",
        description: "Welcome back!",
      });
      
      // Navigate to marketplace after successful login
      navigate('/marketplace');
    } catch (error: any) {
      console.error('Error during sign in:', error);
      toast({
        variant: "destructive",
        title: "Authentication failed",
        description: error.message || "Failed to sign in. Please check your credentials.",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      const supabase = await getSupabaseClient();
      const { error } = await supabase.auth.signUp({ email, password });
      
      if (error) {
        console.error('Sign up error:', error);
        throw error;
      }
      
      toast({
        title: "Account created",
        description: "Please check your email to confirm your account.",
      });
    } catch (error: any) {
      console.error('Error during sign up:', error);
      toast({
        variant: "destructive",
        title: "Registration failed",
        description: error.message || "Failed to create account. Please try again.",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const supabase = await getSupabaseClient();
      await supabase.auth.signOut();
      
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
    } catch (error: any) {
      console.error('Error signing out:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to sign out. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      const supabase = await getSupabaseClient();
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      
      if (error) throw error;
      
      toast({
        title: "Password reset email sent",
        description: "Check your inbox for the password reset link.",
      });
    } catch (error: any) {
      console.error('Error sending password reset:', error);
      toast({
        variant: "destructive",
        title: "Password reset failed",
        description: error.message || "Failed to send password reset email. Please try again.",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthLogin = async (provider: Provider) => {
    try {
      setLoading(true);
      const supabase = await getSupabaseClient();
      const { error } = await supabase.auth.signInWithOAuth({ provider });
      
      if (error) throw error;
      
      // The OAuth redirect will handle the rest
    } catch (error: any) {
      console.error(`Error during ${provider} sign in:`, error);
      toast({
        variant: "destructive",
        title: "Authentication failed",
        description: error.message || `Failed to sign in with ${provider}. Please try again.`,
      });
      setLoading(false);
      throw error;
    }
  };

  const value = {
    session,
    user,
    loading,
    initError,
    signIn,
    signUp,
    signOut,
    resetPassword,
    handleOAuthLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export const useUser = () => {
  const { user } = useAuth();
  return user;
};
